<template>
    <div>
        <v-toolbar dense flat dark color="secondary">
            <v-toolbar-title v-show="hasChatPartner">{{ getSelectedChatUser.displayName }}</v-toolbar-title>
            <v-spacer />
            <v-toolbar-items>
                <PullUpMenu ref="userModal" :label="$t('Nutzer')" icon="mdi-account">
                    <PersonalChatDrawer v-model="pullUpMenuModel" @selected="onSelectUser" />
                </PullUpMenu>
            </v-toolbar-items>
        </v-toolbar>
        <v-card flat outlined class="scroll chatContainer" ref="container" @scroll="handleScroll">
            <v-card-text>
                <v-layout column>
                    <v-flex v-for="(message, index) in messages" :key="`chat-${index}`">
                        <v-layout v-if="isFirstNewMessage(index)">
                            <v-flex align-self-center>
                                <v-divider class="primary" />
                            </v-flex>
                            <v-flex shrink align-start>
                                <span class="primary--text px-2 text-body-2">{{ $t('Neue Nachrichten') }}</span>
                            </v-flex>
                            <v-flex align-self-center>
                                <v-divider class="primary" />
                            </v-flex>
                        </v-layout>
                        <MessageBlock :value="message" />
                    </v-flex>
                </v-layout>
            </v-card-text>
        </v-card>
        <v-card flat outlined>
            <v-card-text>
                <v-text-field
                    v-if="showText"
                    counter="1000"
                    color="accent"
                    clearable
                    :label="$t('Geben Sie eine Nachricht ein ...')"
                    type="text"
                    :hint="$t('Mindestens 1 Zeichen')"
                    prepend-icon="mood"
                    v-model="message"
                    :disabled="!hasChatPartner"
                    :rules="[rules.required, rules.limit]"
                    @keyup.enter.exact="sendPersonalMessage"
                >
                    <template v-slot:append>
                        <v-icon @click="sendPersonalMessage" color="primary" :disabled="!hasMessage">mdi-send</v-icon>
                    </template>
                    <template v-slot:prepend>
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <v-icon v-on="on" @click="showText = !showText">mdi-arrow-expand</v-icon>
                            </template>
                            <span>{{ $t('Textfeld erweitern') }}</span>
                        </v-tooltip>
                    </template>
                </v-text-field>
                <v-textarea
                    v-else
                    counter
                    color="accent"
                    clearable
                    label="Geben Sie eine Nachricht ein ..."
                    type="text"
                    v-model="message"
                    prepend-icon="mood"
                    :hint="$t('Mindestens 1 Zeichen')"
                    :disabled="!hasChatPartner"
                    :rules="[rules.required, rules.limit]"
                    @keyup.enter.exact="sendPersonalMessage"
                    no-resize
                >
                    <template v-slot:append>
                        <v-icon @click="sendPersonalMessage" color="primary" :disabled="!hasMessage">mdi-send</v-icon>
                    </template>
                    <template v-slot:prepend>
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <v-icon v-on="on" @click="showText = !showText">mdi-arrow-collapse</v-icon>
                            </template>
                            <span>{{ $t('Textfeld verkleinern') }}</span>
                        </v-tooltip>
                    </template>
                </v-textarea>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import MessageBlock from '../Helpers/MessageBlock';
import ChatMixins from '../../../Components/Chat/Mixins/ChatMixin';
import PullUpMenu from '../../Generic/PullUpMenu/PullUpMenu.vue';
import PersonalChatDrawer from './PersonalChatDrawer.vue';

export default {
    mixins: [ChatMixins],
    components: { MessageBlock, PullUpMenu, PersonalChatDrawer },
    data() {
        return {
            message: '',
            showText: true,
            showMessageEditor: false,
            maxRenderedMessages: 10,
            rules: {
                required: (v) => v.length >= 1 || this.$t('Mindestens 1 Zeichen'),
                limit: (v) => v.length <= 1000 || this.$t('Maximal 1000 Zeichen')
            }
        };
    },
    watch: {
        getSelectedChatUser({ id: newId }, { id: oldId }) {
            if (newId !== oldId) {
                this.showMessageEditor = true;
                this.maxRenderedMessages = 10;
                this.CHAT_GET_PERSONAL_MESSAGES_BY_ID({ id: newId });
            }
        }
    },
    computed: {
        pullUpMenuModel: {
            get() {
                return this.getSelectedChatUser;
            },
            set(value) {
                this.SET_SELECTED_CHAT_USER(value);
            }
        },
        hasMessage() {
            return !!this.message && this.message.trim().length > 0;
        },
        messages() {
            const array = this.getMessages(this.getSelectedChatUser.id);
            return array.slice(Math.max(array.length - this.maxRenderedMessages, 0));
        },
        hasChatPartner() {
            return Object.keys(this.getSelectedChatUser).length > 0;
        }
    },
    methods: {
        isFirstNewMessage(index) {
            return this.messages.findIndex((message) => message.hasOwnProperty('isNewMessage')) === index;
        },
        handleScroll(value) {
            if (value.target.scrollTop === 0 && Math.max(this.getMessages(this.getSelectedChatUser.id).length - this.maxRenderedMessages, 0) > 0) {
                this.maxRenderedMessages += 1;
                value.target.scrollTop += 100;
            }
        },
        sendPersonalMessage() {
            if (this.message === '' || this.message.length > 1000) {
                return;
            } else {
                this.CHAT_SEND_PERSONAL_MESSAGE({
                    senderId: this.getLoggedInAdmin.id,
                    receiverId: this.getSelectedChatUser.id,
                    content: this.message
                });
                this.message = '';
            }
        },
        scrollDown() {
            this.$vuetify.goTo(0, {
                duration: 500,
                offset: -this.$refs.container.$el.scrollHeight,
                easing: 'easeInOutCubic',
                container: this.$refs.container
            });
            this.maxRenderedMessages += 1;
        },
        onSelectUser() {
            this.$refs.userModal.close();
            this.$nextTick(() => {
                this.$refs.userModal.$parent.$el.querySelector('#v-btn').blur();
            });
        }
    },
    mounted() {
        window.addEventListener('ChatUpdate', this.scrollDown);
        if (Object.keys(this.getSelectedChatUser).length > 0) {
            this.showMessageEditor = true;
            this.maxRenderedMessages = 10;
            this.CHAT_GET_PERSONAL_MESSAGES_BY_ID({
                id: this.getSelectedChatUser.id
            });
        }
    },
    beforeDestroy() {
        window.removeEventListener('ChatUpdate', this.scrollDown);
    }
};
</script>

<style scoped>
.chatContainer {
    --chat-container-max-height: calc(100vh - 366px);
    max-height: var(--chat-container-max-height);
    height: var(--chat-container-max-height);
}
</style>
